const NOMINATIM_API = "https://nominatim.openstreetmap.org";
const PINCODE_API = "https://api.postalpincode.in/pincode";

export const reverseGeocode = async (lat, lng) => {
  try {
    const response = await fetch(
      `${NOMINATIM_API}/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
      {
        headers: {
          "Accept-Language": "en",
          "User-Agent": "YourAppName/1.0",
        },
      },
    );

    if (!response.ok) throw new Error("Reverse geocoding failed");

    const data = await response.json();

    if (data?.address) {
      const address = data.address;
      return {
        fullAddress: data.display_name,
        shortAddress:
          `${address.road || address.suburb || address.village || address.city || ""}, ${address.city || address.state_district || ""}, ${address.state || ""}`.replace(
            /^, |, ,/,
            "",
          ),
        city:
          address.city ||
          address.town ||
          address.village ||
          address.district ||
          "",
        state: address.state || "",
        pincode: address.postcode || "",
        country: address.country || "India",
        lat: lat,
        lng: lng,
      };
    }
    return null;
  } catch (error) {
    console.error("Reverse geocode error:", error);
    return null;
  }
};
/* ---------------- SEARCH PLACES ---------------- */
export const searchPlaces = async (query) => {
  if (!query || query.length < 3) return [];

  try {
    const response = await fetch(
      `${NOMINATIM_API}/search?format=json&q=${encodeURIComponent(query)}&addressdetails=1&limit=5`,
      {
        headers: {
          "Accept-Language": "en",
          "User-Agent": "YourAppName/1.0",
        },
      },
    );

    if (!response.ok) throw new Error("Search failed");

    const data = await response.json();

    return data.map((item) => ({
      place_id: item.place_id,
      display_name: item.display_name,
      lat: item.lat,
      lon: item.lon,
      type: item.type,
      class: item.class,
    }));
  } catch (error) {
    console.error("Search places error:", error);
    return [];
  }
};

/* ---------------- GET CURRENT LOCATION ---------------- */
export function getCurrentLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation not supported"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      (err) => {
        reject(err);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  });
}

export const isGPSupported = () => {
  return "geolocation" in navigator;
};

// Check GPS permission status
export const checkGPSPermission = async () => {
  if (!navigator.permissions) {
    return "unsupported";
  }

  try {
    const result = await navigator.permissions.query({ name: "geolocation" });
    return result.state; // 'granted', 'denied', 'prompt'
  } catch (error) {
    console.error("Permission check error:", error);
    return "unknown";
  }
};

// Get current position from GPS
export const getCurrentPosition = () => {
  return new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      });
    } else {
      reject(new Error("Geolocation is not supported by your browser"));
    }
  });
};

export const reverseGeocodeAddresses = async (latitude, longitude) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
      {
        headers: {
          "Accept-Language": "en-IN", // Indian address format
          "User-Agent": "MyShoppingApp/1.0",
        },
      },
    );

    if (!response.ok) {
      throw new Error("Geocoding failed");
    }

    const data = await response.json();
    const address = data.address;

    return {
      fullAddress: data.display_name,
      houseNumber: address.house_number || "",
      road: address.road || "",
      city: address.city || address.town || address.village || "",
      state: address.state || "",
      pincode: address.postcode || "",
      country: address.country || "",
      latitude: latitude,
      longitude: longitude,
      formatted: `${address.road || ""}, ${address.city || ""} - ${address.postcode || ""}`,
      landmark: address.neighbourhood || address.suburb || "",
    };
  } catch (error) {
    console.error("Reverse geocoding error:", error);
    throw new Error("Could not get address from location");
  }
};

export const geocodeAddress = async (address) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
    );

    if (!response.ok) {
      throw new Error("Geocoding failed");
    }

    const data = await response.json();
    if (data.length === 0) {
      throw new Error("Address not found");
    }

    return {
      latitude: Number.parseFloat(data[0].lat),
      longitude: Number.parseFloat(data[0].lon),
    };
  } catch (error) {
    console.error("Forward geocoding error:", error);
    throw new Error("Could not find location");
  }
};

export const saveAddressToLocal = (address) => {
  try {
    localStorage.setItem("deliveryAddress", JSON.stringify(address));
    return true;
  } catch (error) {
    console.error("Error saving address:", error);
    return false;
  }
};

export const getAddressFromLocal = () => {
  try {
    const saved = localStorage.getItem("deliveryAddress");
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.error("Error loading address:", error);
    return null;
  }
};

// Format address for display
export const formatAddressDisplay = (address) => {
  if (!address) return "No address selected";

  if (address.fullAddress) {
    return (
      address.fullAddress.substring(0, 50) +
      (address.fullAddress.length > 50 ? "..." : "")
    );
  }

  if (address.city && address.pincode) {
    return `${address.city} - ${address.pincode}`;
  }

  return address.formatted || "Address selected";
};

export const reverseGeocodeAddress = reverseGeocode;

// Validate pincode (Indian pincode: 6 digits)
export const validatePincode = (pincode) => {
  const pincodeRegex = /^[1-9][0-9]{5}$/;
  return pincodeRegex.test(pincode);
};

// Get address by pincode
export const getAddressByPincode = async (pincode) => {
  if (!validatePincode(pincode)) {
    throw new Error("Invalid pincode");
  }
  
  try {
    const response = await fetch(`${PINCODE_API}/${pincode}`);
    const data = await response.json();
    
    if (data?.[0]?.Status === "Success") {
      const postOffice = data[0].PostOffice[0];
      return {
        area: postOffice.Name,
        city: postOffice.District,
        state: postOffice.State,
        country: postOffice.Country,
        pincode: pincode
      };
    }
    throw new Error("Pincode not found");
  } catch (error) {
    console.error("Pincode API error:", error);
    throw error;
  }
};

// Get GPS error message
export const getGPSErrorMessage = (error) => {
  switch(error.code) {
    case error.PERMISSION_DENIED:
      return "Location access denied. Please enable location permissions.";
    case error.POSITION_UNAVAILABLE:
      return "Location information unavailable. Please check your GPS.";
    case error.TIMEOUT:
      return "Location request timed out. Please try again.";
    default:
      return "Unable to get your location. Please try searching manually.";
  }
};

export const isSameAddress = (addr1, addr2) => {
  if (!addr1 || !addr2) return false;
  return (
    addr1.pincode === addr2.pincode &&
    addr1.city === addr2.city &&
    addr1.latitude === addr2.latitude
  );
};

export const getDistanceBetweenPoints = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};
